import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import lodash from 'lodash';
import SearchIcon from '@mui/icons-material/Search';
import { Checkbox, FormControlLabel } from '@mui/material';
import { Guid } from 'guid-typescript';

// COMPONENTS
import SecondaryButton from '@/main/components/shared/button/secondaryButton';
import Button from '@/main/components/shared/button/button';
import ContainerBox from '../../components/containerBox/containerBox';

// SERVICES
import { KeyValue } from '@/main/models/common';
import {
  AddressRequest,
  BodyShopFilterRequest,
  InsurerFilterRequest,
  ManufacturerRequest,
  ModelRequest,
  SubModelRequest
} from '@/main/models/request';
import { HeadQuarterBranchFilterEnum } from '@/main/services/enum/HeadQuarterBranchFilterEnum';
import {
  QuoteResponse,
  VehicleAutoCompleteResponse
} from '@/main/models/response';
import { StatusRegisterEnum } from '@/main/services/enum/StatusRegisterEnum';
import { OrderSaveRequest } from '@/main/models/request/orderRequest';
import { quoteApi } from '@/main/api';
import { OrderStepsEnum } from '@/main/services/enum/OrderStepsEnum';
import {
  getDefaultCountryOptionDrop,
  getDefaultOptionDrop,
  getTranslateByKey,
  isEmpty,
  isNullUndefined,
  removeEmptyValues
} from '@/main/services/utils';
import {
  userIsAdmin,
  userIsBodyshop,
  userIsInsurer
} from '@/main/services/utilsUser';
import { CustomError } from '@/main/services/class/customError';

// REDUX
import { getCountriesUser, useCountryReducer } from '@/main/store/country';
import {
  getBodyshops,
  resetReducerBodyshops,
  useBodyshopReducer
} from '@/main/store/bodyshop';
import {
  getAddressByBodyshop,
  resetReducerAddressByBodyshop,
  useAddressByBodyshopReducer
} from '@/main/store/addressByBodyshop';
import {
  getYears,
  resetReducerYears,
  useYearReducer
} from '@/main/store/vehicle/year';
import {
  getManufacturers,
  resetReducerManufacturers,
  useManufacturerReducer
} from '@/main/store/vehicle/manufacturer';
import {
  getSubModels,
  resetReducerSubModels,
  useSubModelReducer
} from '@/main/store/vehicle/subModel';
import { getDamageAreas, useDamageAreaReducer } from '@/main/store/damageArea';
import {
  getModels,
  resetReducerModels,
  useModelReducer
} from '@/main/store/vehicle/model';
import {
  getInsurersFull,
  resetReducerInsurersFull,
  useInsurerReducer
} from '@/main/store/insurer';

// SCREENS/COMPONENTS/CONTEXT/STYLES LOCAL
import { useCreateOrderContext } from '../../context';
import KogModalFadeComponent from '@/main/components/modal/ModalFadeComponent';
import ClaimInformation from './claimInformation/claimInformation';
import BillingOrDeliveryAddress from './billingOrDeliveryAddress/billingOrDeliveryAddress';
import VehicleInformation from './vehicleInformation/vehicleInformation';
import * as S from './styles';
import { ModalConfirm } from './modalConfirm/modalConfirm';
import { QuoteClaimNumberValidationRequest } from '@/main/models/request/quoteRequest';
import { useCartReducer } from '@/main/store/cart';
import { RoutesConstants } from '@/main/routes/routesConstants';
import { useNavigate } from 'react-router-dom';
import { useQueryParamsReducer } from '@/main/store/queryParams';
import { KOGAreaButtons } from '@/main/components/shared/styles';


export interface IValidateForm {
  insurer: KeyValue | null;
  bodyshop: KeyValue;
  claimNumber?: string | null;
  deliveryAddress: string | number | null;
  year: KeyValue;
  manufacturer: KeyValue;
  model: KeyValue;
  trim: KeyValue;
  licensePlate?: string;
  vin?: string;
  vehicleColor?: string;
  numbersOfDoor?: string;
}

const INITIAL_FORM_STATE = {
  insurer: null as KeyValue | null,
  bodyshop: null as KeyValue | null,
  claimNumber: '',
  deliveryAddress: null as string | number | null,
  year: null as KeyValue | null,
  manufacturer: null as KeyValue | null,
  model: null as KeyValue | null,
  trim: null as KeyValue | null,
  licensePlate: '',
  vin: '',
  vehicleColor: '',
  numbersOfDoor: '',
  guidAddressBodyShop: null as string | null,
  front: false,
  left: false,
  right: false,
  rear: false,
  top: false
};

export type ICreateOrderForm = typeof INITIAL_FORM_STATE;

function StepOneScreen() {
  const countryReducer = useCountryReducer();
  const insurerReducer = useInsurerReducer();
  const bodyshopReducer = useBodyshopReducer();
  const damageAreaReducer = useDamageAreaReducer();
  const addressByBodyshopReducer = useAddressByBodyshopReducer();
  const yearReducer = useYearReducer();
  const manufacturerReducer = useManufacturerReducer();
  const modelReducer = useModelReducer();
  const subModelReducer = useSubModelReducer();
  const [bodyshopValue, setBodyshopValue] = useState(''); 
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    reset,
    clearErrors,
    formState: { errors },
    watch
  } = useForm<ICreateOrderForm>({
    mode: 'onChange',
    defaultValues: INITIAL_FORM_STATE
  });
  const {
    isLoadingInfoOrder,
    guidQuote,
    savedData,
    integration,
    isQuoteOpen,
    isQuoteManual,
    isQuoteCanceled,
    setCurrentStep,
    handleSaveStepData,
    displayModalCancel,
    updateGuidQuote,
    loadInfoOrder
  } = useCreateOrderContext();

  // Methods Initial Load State
  const loadOptionsSelectedDamageArea = () => {
    const findDataCurrentStep = savedData.find(
      item => item.idStep === OrderStepsEnum.VehicleInformation
    );
    const optionsSelected = findDataCurrentStep?.data?.selectedDamageArea || [];
    return optionsSelected;
  };
  const [
    optionsSelectedDeliveryAddress,
    setOptionsSelectedDeliveryAddress
  ] = useState<string | null>(null);
  const [optionsSelectedDamageArea, setOptionsSelectedDamageArea] = useState<
    string[]
  >(loadOptionsSelectedDamageArea);
  const [isLoadingSearchSave, setIsLoadingSearchSave] = useState(false);
  const [openModalConfirm, setOpenModalConfirm] = useState(false);
  const optionDefaultCountryDrop = getDefaultCountryOptionDrop(
    countryReducer.countriesUser.data
  );

  const optionDefaultBodyShopDrop = getDefaultOptionDrop(
    bodyshopReducer.bodyshops.data
  );
  const cartReducer = useCartReducer();

  const hasItemsOfCarts =
    cartReducer.cartItems.data !== null &&
    cartReducer.cartItems.data.length > 0;

  const isUserInsurerLogged = userIsInsurer();
  const isUserBodyShopLogged = userIsBodyshop();
  const isUserAdminLogged = userIsAdmin();

  const watcherBodyshop = watch('bodyshop');
  const isEditQuote =
    guidQuote !== undefined && guidQuote !== null && guidQuote !== Guid.EMPTY;

  const isDisplayedDamageAreaDrop = () =>
    damageAreaReducer.damageAreas.data === null || isQuoteCanceled;

  const isDisabledYearDrop = () =>
    yearReducer.years.data === null ||
    hasItemsOfCarts ||
    isQuoteCanceled ||
    isEditQuote;

  const isDisabledManufacturerDrop = () =>
    manufacturerReducer.manufacturers.data === null ||
    yearReducer.years.data === null ||
    hasItemsOfCarts ||
    isQuoteCanceled ||
    isEditQuote;

  const isDisabledModelDrop = () =>
    modelReducer.models.data === null ||
    manufacturerReducer.manufacturers.data === null ||
    yearReducer.years.data === null ||
    hasItemsOfCarts ||
    isQuoteCanceled ||
    isEditQuote;

  const isDisabledSubModelDrop = () =>
    subModelReducer.subModels.data === null ||
    modelReducer.models.data === null ||
    manufacturerReducer.manufacturers.data === null ||
    yearReducer.years.data === null ||
    hasItemsOfCarts ||
    isQuoteCanceled ||
    isEditQuote;

  const isDisabledInsurerDrop = () =>
    !!guidQuote || !!(isUserInsurerLogged && optionDefaultInsurerDrop);
  const isDisabledBodyShopDrop = () =>
    !!guidQuote ||
    !!(isUserInsurerLogged && optionDefaultBodyShopDrop) ||
    !!(isUserBodyShopLogged && optionDefaultBodyShopDrop);

  const optionDefaultInsurerDrop = getDefaultOptionDrop(
    insurerReducer.insurersFull.data
  );
  // Async functions
  function loadOptionsByVehicle(vehicle: VehicleAutoCompleteResponse) {
    const {
      guidYear,
      year,
      guidVehicleManufacturer,
      manufacturer,
      guidVehicleModel,
      model,
      guidSubModel,
      subModel
    } = vehicle;
    if (guidYear) {
      clearErrors();
      setValue('year', {
        key: guidYear,
        value: year
      });
      dispatch(getYears());

      if (guidVehicleManufacturer) {
        setValue('manufacturer', {
          key: guidVehicleManufacturer,
          value: manufacturer
        });
        dispatch(getManufacturers({ guidYear }));

        if (guidVehicleModel) {
          setValue('model', {
            key: guidVehicleModel,
            value: model
          });
          dispatch(
            getModels({ guidManufacturer: guidVehicleManufacturer, guidYear })
          );

          if (guidSubModel) {
            setValue('trim', {
              key: guidSubModel,
              value: subModel
            });
            dispatch(
              getSubModels({
                guidCountry: String(optionDefaultCountryDrop?.key),
                guidManufacturer: guidVehicleManufacturer,
                guidYear,
                guidModel: guidVehicleModel
              })
            );
          } else {
            dispatch(resetReducerSubModels());
            setValue('trim', null);
          }
        } else {
          dispatch(resetReducerModels());
          setValue('model', null);
        }
      } else {
        dispatch(resetReducerManufacturers());
        setValue('manufacturer', null);
      }
    } else {
      dispatch(resetReducerYears());
      setValue('year', null);
    }
  }

  function callInsurerApiDomain(companySelected: KeyValue | null = null) {
    // Clean fields & reducers
    dispatch(resetReducerInsurersFull());

    if (optionDefaultCountryDrop !== null) {
      const request: InsurerFilterRequest = {
        ...(companySelected && { guidBodyShop: String(companySelected.key) }),
        guidCountry: String(optionDefaultCountryDrop?.key),
        type: HeadQuarterBranchFilterEnum.Only_Headquarters,
        idStatusRegister: StatusRegisterEnum.Active
      };
      dispatch(getInsurersFull(request));
    }
  }

  function callBodyshopApiDomain(insurerSelected: KeyValue | null = null) {
    // Clean fields & reducers
    dispatch(resetReducerBodyshops());

    if (optionDefaultCountryDrop !== null) {
      const request: BodyShopFilterRequest = {
        ...(insurerSelected && { guidInsurer: String(insurerSelected.key) }),
        guidCountry: String(optionDefaultCountryDrop?.key),
        type: HeadQuarterBranchFilterEnum.All,
        idStatusRegister: StatusRegisterEnum.Active
      };
      dispatch(getBodyshops(request));
    }
  }

  function callAddressByBodyshopApiDomain(
    optionSelected: string | number | null | undefined
  ) {
    dispatch(resetReducerAddressByBodyshop());
    if (optionSelected !== null && optionSelected !== undefined) {
      const request: AddressRequest = {
        guidBodyshop: String(optionSelected)
      };
      dispatch(getAddressByBodyshop(request));
    }
  }

  function existsInDrop(data: KeyValue[], nameField: any) {
    const fieldKeySelected = getValues(nameField)?.key;
    const existsInData = data.find(item => item.key === fieldKeySelected);

    if (!existsInData) {
      setValue(nameField, null);
    }
  }

  useEffect(() => {
    if (
      insurerReducer.insurersFull.data !== null &&
      !insurerReducer.insurersFull.isLoading
    ) {
      const isTypeCompanyUserInsurer = userIsInsurer();
      existsInDrop(insurerReducer.insurersFull.data, 'insurer');
      if (isTypeCompanyUserInsurer && optionDefaultInsurerDrop)
        callBodyshopApiDomain(optionDefaultInsurerDrop);
    }
  }, [insurerReducer.insurersFull.data, insurerReducer.insurersFull.isLoading]);

  useEffect(() => {
    if (
      bodyshopReducer.bodyshops.data &&
      !bodyshopReducer.bodyshops.isLoading
    ) {
      const isTypeCompanyUserBodyshop = userIsBodyshop();
      existsInDrop(bodyshopReducer.bodyshops.data, 'bodyshop');
      if (isTypeCompanyUserBodyshop && optionDefaultBodyShopDrop)
        callInsurerApiDomain(optionDefaultBodyShopDrop);
    }
  }, [bodyshopReducer.bodyshops.data, bodyshopReducer.bodyshops.isLoading]);

  useEffect(() => {
    callAddressByBodyshopApiDomain(watcherBodyshop?.key);
  }, [watcherBodyshop]);

  function callManufacturerApiDomain(optionSelected: KeyValue | null) {
    setValue('manufacturer', null);
    setValue('model', null);
    setValue('trim', null);
    dispatch(resetReducerManufacturers());
    dispatch(resetReducerModels());
    dispatch(resetReducerSubModels());

    if (optionSelected !== null) {
      const request: ManufacturerRequest = {
        guidYear: String(optionSelected.key)
      };
      dispatch(getManufacturers(request));
    }
  }

  function callModelApiDomain(optionSelected: KeyValue | null) {
    setValue('model', null);
    setValue('trim', null);
    dispatch(resetReducerModels());
    dispatch(resetReducerSubModels());

    if (optionSelected !== null) {
      const request: ModelRequest = {
        guidYear: String(getValues().year?.key),
        guidManufacturer: String(optionSelected.key)
      };
      dispatch(getModels(request));
    }
  }

  function callSubModelApiDomain(optionSelected: KeyValue | null) {
    setValue('trim', null);
    dispatch(resetReducerSubModels());

    if (optionSelected !== null) {
      const request: SubModelRequest = {
        guidCountry: String(optionDefaultCountryDrop?.key),
        guidYear: String(getValues().year?.key),
        guidManufacturer: String(String(getValues().manufacturer?.key)),
        guidModel: String(optionSelected.key)
      };
      dispatch(getSubModels(request));
    }
  }

  function loadSavedData() {
    const savedDataCurrentStep = savedData.find(
      item => item.idStep === OrderStepsEnum.VehicleInformation
    );

    if (savedDataCurrentStep && savedDataCurrentStep.data) {
      const dataCurrentStep: QuoteResponse = savedDataCurrentStep.data;
      reset(INITIAL_FORM_STATE);

      // Load options
      dispatch(getManufacturers({ guidYear: dataCurrentStep.vehicleGuidYear }));
      dispatch(
        getModels({
          guidYear: dataCurrentStep.vehicleGuidYear,
          guidManufacturer: dataCurrentStep.vehicleGuidManufacturer
        })
      );
      dispatch(
        getSubModels({
          guidCountry: String(dataCurrentStep.guidCountry),
          guidYear: dataCurrentStep.vehicleGuidYear,
          guidManufacturer: dataCurrentStep.vehicleGuidManufacturer,
          guidModel: dataCurrentStep.vehicleGuidVehicleModel
        })
      );

      // Fields required
      setValue('bodyshop', {
        key: dataCurrentStep.guidBodyShop,
        value: dataCurrentStep.bodyShop
      });
      setValue('year', {
        key: dataCurrentStep.vehicleGuidYear,
        value: dataCurrentStep.vehicleYear
      });
      setValue('manufacturer', {
        key: dataCurrentStep.vehicleGuidManufacturer,
        value: dataCurrentStep.vehicleManufacturer
      });
      setValue('model', {
        key: dataCurrentStep.vehicleGuidVehicleModel,
        value: dataCurrentStep.vehicleModel
      });
      setValue('trim', {
        key: dataCurrentStep.vehicleGuidSubModel,
        value: dataCurrentStep.vehicleSubModel
      });

      // Fields not required
      if (dataCurrentStep.insurer && dataCurrentStep.guidInsurer)
        setValue('insurer', {
          key: String(dataCurrentStep.guidInsurer),
          value: dataCurrentStep.insurer
        });
      if (dataCurrentStep.claimNumber)
        setValue('claimNumber', dataCurrentStep.claimNumber);
      if (dataCurrentStep.vehicleLicensePlate)
        setValue('licensePlate', dataCurrentStep.vehicleLicensePlate);
      if (dataCurrentStep.vehicleVIN)
        setValue('vin', dataCurrentStep.vehicleVIN);
      if (dataCurrentStep.vehicleColor)
        setValue('vehicleColor', dataCurrentStep.vehicleColor);
      if (dataCurrentStep.vehicleNumDoors)
        setValue('numbersOfDoor', dataCurrentStep.vehicleNumDoors);
      if (dataCurrentStep.guidAddressBodyShop) {
        setValue('guidAddressBodyShop', dataCurrentStep.guidAddressBodyShop);
      }
    }
  }

  // Handle functions
  function validateForm(data: IValidateForm) {
    const messages = [];
    if (!data.bodyshop)
      messages.push(getTranslateByKey('information.bodyshopRequired', true));
    if (optionsSelectedDeliveryAddress === null)
      messages.push(
        getTranslateByKey('information.mustHaveAtLeastOneDeliveryAddress', true)
      );
    if (!data.year)
      messages.push(getTranslateByKey('information.yearRequired', true));
    if (!data.manufacturer)
      messages.push(
        getTranslateByKey('information.manufacturerRequired', true)
      );
    if (!data.model)
      messages.push(getTranslateByKey('information.modelRequired', true));
    if (!data.trim)
      messages.push(getTranslateByKey('information.trimRequired', true));

    if (messages.length > 0) {
      messages.map(message => toast.error(message));
      throw new CustomError(messages.join('/n'), 'MISSING_FIELDS');
    }
    return true;
  }

  function handleToggleCheckbox(guid: string) {
    if ((!integration && !isQuoteOpen) || (!integration && !isQuoteManual)) {
      const cloneOptionsSelected = lodash.cloneDeep(optionsSelectedDamageArea);
      const index = cloneOptionsSelected.indexOf(guid);

      if (index > -1) {
        cloneOptionsSelected.splice(index, 1);
      } else {
        cloneOptionsSelected.push(guid);
      }

      setOptionsSelectedDamageArea(cloneOptionsSelected);
    }
  }

  async function handleSearchParts(data: ICreateOrderForm) {
    try {
      setIsLoadingSearchSave(true);
      validateForm(data as IValidateForm);
      const vehicleData = {
        guidVehicleManufacturer: String(data?.manufacturer?.key),
        guidVehicleModel: String(data?.model?.key),
        guidVehicleSubModel: String(data?.trim?.key),
        guidVehicleYear: String(data?.year?.key)
      };

      const requestData = removeEmptyValues<OrderSaveRequest>({
        ...(guidQuote && { guidQuote: String(guidQuote) }), //is edit
        guidInsurer: data.insurer ? String(data.insurer.key) : null,
        guidBodyShop: String(data.bodyshop?.key),
        guidCompanyAddress: String(optionsSelectedDeliveryAddress),
        claimNumber: data.claimNumber,
        vehicle: vehicleData,
        vehicleRegistration: data.licensePlate,
        vin: data.vin,
        color: data.vehicleColor,
        numDoors: data.numbersOfDoor ? Number(data.numbersOfDoor) : null,
        buyParts: true
      });

      const response = guidQuote
        ? await quoteApi.updateQuote(requestData)
        : await quoteApi.saveQuote(requestData);
      const { result } = response.data;

      const messageConfirmation = guidQuote
        ? getTranslateByKey('information.updatedOrderSuccessfully')
        : getTranslateByKey('information.createdOrderSuccessfully');

      toast.success(messageConfirmation);
      const dataStep = {
        vehicleGuidManufacturer: String(data?.manufacturer?.key),
        vehicleGuidVehicleModel: String(data?.model?.key),
        vehicleGuidSubModel: String(data?.trim?.key),
        vehicleGuidYear: String(data?.year?.key),
        vehicleYear: String(data?.year?.value),
        vehicleManufacturer: String(data?.manufacturer?.value),
        vehicleModel: String(data?.model?.value),
        vehicleSubModel: String(data?.trim?.value),
        insurer: data.insurer ? String(data.insurer.value) : null,
        guidInsurer: data.insurer ? String(data.insurer.key) : null,
        guidBodyShop: String(data.bodyshop?.key),
        bodyShop: String(data.bodyshop?.value),
        guidAddressBodyShop: String(optionsSelectedDeliveryAddress),
        claimNumber: data.claimNumber,
        vehicleLicensePlate: data.licensePlate,
        vehicleVIN: data.vin,
        vehicleColor: data.vehicleColor,
        vehicleNumDoors: data.numbersOfDoor ? Number(data.numbersOfDoor) : null,
        selectedDamageArea: optionsSelectedDamageArea,
        guidCompanyBuyer: result.guidCompanyBuyer,
        hasAcessDataHistorical: result.hasAcessDataHistorical
      };
      updateGuidQuote(result.guidQuote);
      handleSaveStepData(OrderStepsEnum.VehicleInformation, true, dataStep);
      handleSaveStepData(OrderStepsEnum.Parts, false, null);
      handleSaveStepData(OrderStepsEnum.Summary, false, null);
      setCurrentStep(OrderStepsEnum.Parts);
      loadInfoOrder(result.guidQuote);
    } catch (error) {
    } finally {
      setIsLoadingSearchSave(false);
      setOpenModalConfirm(false);
    }
  }

  const { queryParams } = useQueryParamsReducer();
  function handleClickBack(e: React.KeyboardEvent<any>) {
    if (e.ctrlKey) {
      const saveFilters = !isNullUndefined(queryParams.data) ? queryParams.data[RoutesConstants.orderSearchOrder] : null;

      const params = new URLSearchParams();

      if (!isNullUndefined(saveFilters)) {
        Object.keys(saveFilters).forEach(key => {
          params.append(key, saveFilters[key]);
        });
      }

      const formattedParams = params.size > 0 ? `?${params}` : '';
      return window.open(`#${RoutesConstants.orderSearchOrder}${formattedParams}`);
    } else {
      return navigate(
        `${RoutesConstants.orderSearchOrder}`
      );
    }
  }

  useEffect(() => {
    if (countryReducer.countriesUser.data === null)
      dispatch(getCountriesUser());
    if (damageAreaReducer.damageAreas.data === null) dispatch(getDamageAreas());
    if (yearReducer.years.data === null) dispatch(getYears());
  }, []);

  useEffect(() => {
    if (optionDefaultCountryDrop) {
      callInsurerApiDomain();
      callBodyshopApiDomain();
    }
  }, [optionDefaultCountryDrop]);
  useEffect(() => {
    if (guidQuote && !isLoadingInfoOrder) {
      loadSavedData();
    }
  }, [isLoadingInfoOrder]);

  const handleValidationSubmit = (data: ICreateOrderForm) => {
    const requestClaimNumberValidation: QuoteClaimNumberValidationRequest = {
      claimNumber: getValues('claimNumber'),
      guidInsurer:
        getValues('insurer.key') !== null
          ? String(getValues('insurer.key'))
          : null,
      guidQuote: guidQuote !== null ? String(guidQuote) : null
    };

    const requestFormatted = removeEmptyValues(requestClaimNumberValidation);

    quoteApi.getValidClaimNumber(requestFormatted).then(response => {
      if (response.data.result) {
        setOpenModalConfirm(true);
      } else {
        handleSearchParts(data);
      }
    });
  };

  return (
    <>
      <form>
        <S.ContainerBox>
          <ClaimInformation
            control={control}
            errors={errors}
            setValue={setValue}
            insurerReducer={insurerReducer}
            bodyshopReducer={bodyshopReducer}
            callBodyshopApiDomain={callBodyshopApiDomain}
            callInsurerApiDomain={callInsurerApiDomain}
            isDisabledInsurerDrop={isDisabledInsurerDrop}
            isDisabledBodyShopDrop={isDisabledBodyShopDrop}
            integration={integration}
            isQuoteOpen={isQuoteOpen}
            isQuoteManual={isQuoteManual}
            isQuoteCanceled={isQuoteCanceled}
            isUserAdminLogged={isUserAdminLogged}
          />

          <BillingOrDeliveryAddress
            addressData={addressByBodyshopReducer.addressByBodyshop.data}
            isLoading={addressByBodyshopReducer.addressByBodyshop.isLoading}
            optionsSelectedDeliveryAddress={optionsSelectedDeliveryAddress}
            setOptionsSelectedDeliveryAddress={
              setOptionsSelectedDeliveryAddress
            }
            hasItemsOfCarts={hasItemsOfCarts}
            label={getTranslateByKey('information.deliveryAddress')}
            isBillingAddress={false}
            guidAddressSaved={getValues('guidAddressBodyShop')}
            integration={integration}
            isQuoteOpen={isQuoteOpen}
            isQuoteManual={isQuoteManual}
            isQuoteCanceled={isQuoteCanceled}
          />

          <VehicleInformation
            control={control}
            errors={errors}
            setValue={setValue}
            loadOptionsByVehicle={loadOptionsByVehicle}
            yearReducer={yearReducer}
            isDisabledYearDrop={isDisabledYearDrop}
            manufacturerReducer={manufacturerReducer}
            isDisabledManufacturerDrop={isDisabledManufacturerDrop}
            callManufacturerApiDomain={callManufacturerApiDomain}
            modelReducer={modelReducer}
            callModelApiDomain={callModelApiDomain}
            callSubModelApiDomain={callSubModelApiDomain}
            isDisabledModelDrop={isDisabledModelDrop}
            subModelReducer={subModelReducer}
            isDisabledSubModelDrop={isDisabledSubModelDrop}
            hasItemsOfCarts={hasItemsOfCarts}
            integration={integration}
            isQuoteCanceled={isQuoteCanceled}
            isEditQuote={isEditQuote}
          />

          {!isDisplayedDamageAreaDrop() && (
            <ContainerBox
              title={getTranslateByKey('information.damageArea')}
              id="id-createOrder-checkbox-damageArea">
              <S.RowCheckBox>
                {damageAreaReducer?.damageAreas?.data?.map(area => {
                  const key = String(area.key);
                  return (
                    <FormControlLabel
                      key={key}
                      label={area.value}
                      control={
                        <Checkbox
                          color="primary"
                          checked={optionsSelectedDamageArea.includes(key)}
                          onChange={() => handleToggleCheckbox(key)}
                          name={`${area.key}`}
                        />
                      }
                    />
                  );
                })}
              </S.RowCheckBox>
            </ContainerBox>
          )}

          <KOGAreaButtons>
            <SecondaryButton
              type="button"
              onClick={(event) => handleClickBack(event)}
              id="id-createOrder-button-back">
              {getTranslateByKey('information.back')}
            </SecondaryButton>
            <SecondaryButton
              type="button"
              onClick={() => displayModalCancel()}
              id="id-createOrder-button-cancel"
              disabled={(integration && isQuoteOpen) || isQuoteCanceled}>
              {getTranslateByKey('information.cancel')}
            </SecondaryButton>
            <Button
              type="button"
              isLoading={isLoadingSearchSave}
              onClick={handleSubmit(handleValidationSubmit)}
              id="id-createOrder-button-searchParts">
              {getTranslateByKey('information.searchParts')}
              {!isLoadingSearchSave && (
                <SearchIcon style={{ marginLeft: 10 }} />
              )}
            </Button>
          </KOGAreaButtons>

          <KogModalFadeComponent
            open={openModalConfirm}
            setOpen={setOpenModalConfirm}>
            <ModalConfirm
              handleConfirm={handleSubmit(handleSearchParts)}
              setOpenModalConfirm={setOpenModalConfirm}
            />
          </KogModalFadeComponent>
        </S.ContainerBox>
      </form>
    </>
  );
}
export default StepOneScreen;
